import React, { FC, useCallback, useMemo } from 'react'
import { Table } from '@wmgtech/legato'
import { ColumnsType } from '@wmgtech/legato/build/components/Table/types'
import { useFormContext } from 'react-hook-form'
import get from 'lodash/get'
import { IIsaacLink } from 'shared/interfaces'
import { FormInput } from 'components/Form'

import { useConfirmation } from 'components/Project/Confirmation'
import { useRow } from 'components/Project/Tables'
import { EditMode, ViewMode } from './ActionButtons'
import { IDeleteArguments, IIsaacTableProps } from './IsaacTable.props'

import styles from './isaac-table.module.scss'

export const IsaacTable: FC<IIsaacTableProps> = ({
  hidden,
  data,
  onDelete,
  prefix,
  onSubmitChange,
  isLocalizing,
  isGlobal = false
}) => {
  const { condition, onRow, control, onEdit } = useRow('editRow')
  const { setValue, trigger, getValues, reset, formState } = useFormContext()

  const ISAACTitle = `${isGlobal ? 'Global ' : !isLocalizing ? 'Local ' : ''}ISAAC Link`

  const [onConfirmDelete, deleteModal] = useConfirmation<IDeleteArguments>(onDelete, {
    header: 'Delete Local ISAAC Link',
    createMessage: (text) => (
      <>
        Are you sure you want to delete <b>{text}</b>?
      </>
    ),
    actionText: 'Okay'
  })

  const [onConfirmCancel, cancelModal] = useConfirmation<string>(
    () => {
      reset()
    },
    {
      header: 'Cancel',
      createMessage: () => <>Are you sure you want to cancel? Changes will not be saved.</>,
      actionText: 'Okay'
    }
  )

  const handleCancel = useCallback((): boolean => {
    const idx = getValues('editRow')
    const path = [prefix, idx].join('.')
    const currentLink = getValues(path)
    const link = get(formState, ['defaultValues', prefix, idx])
    if (link && (currentLink.title !== link.title || currentLink.url !== link.url)) {
      onConfirmCancel({ data: '', message: '' })
      return false
    } else {
      reset()
      return true
    }
  }, [formState, getValues, onConfirmCancel, prefix, reset])

  const handleEdit = useCallback(
    (idx: number) => {
      const values = getValues()
      reset({ ...values })
      if (typeof values.editRow === 'number' && !isNaN(values.editRow)) {
        if (handleCancel()) {
          onEdit(idx)
        }
      } else {
        onEdit(idx)
      }
    },
    [getValues, handleCancel, onEdit, reset]
  )

  const submitEdit = useCallback(
    (idx: number) => {
      const path = `${prefix}.${idx}`
      trigger(path)
        .then((isValid) => {
          if (isValid) {
            setValue('editRow', undefined)
            const { title } = getValues(path)
            const link = get(formState, ['defaultValues', prefix, idx])
            if (typeof onSubmitChange === 'function') {
              onSubmitChange(typeof link === 'undefined' ? 'append' : 'update', title)
            }
          }
        })
        .catch((err) => {
          console.warn(err)
        })
    },
    [formState, getValues, onSubmitChange, prefix, setValue, trigger]
  )

  const columns: ColumnsType<IIsaacLink>[] = useMemo(
    () => [
      {
        title: 'Description',
        dataIndex: 'title',
        key: 'titleDescription',
        sortable: false,
        width: '50%',
        align: 'left',
        render: condition([
          (_, __, i) => getValues(`${prefix}.${i}.title`),
          (_, __, i) => (
            <FormInput control={control} fieldName={`${prefix}.${i}.title`} placeholder="Type Description" />
          )
        ])
      },
      {
        title: ISAACTitle,
        dataIndex: 'url',
        key: 'description',
        sortable: false,
        width: '50%',
        align: 'left',
        render: condition([
          (_, __, i) => getValues(`${prefix}.${i}.url`),
          (_, __, i) => <FormInput control={control} fieldName={`${prefix}.${i}.url`} placeholder={ISAACTitle} />
        ])
      },
      {
        dataIndex: '',
        key: 'action',
        sortable: false,
        width: 90,
        render: condition([
          (_, r, idx) => (
            <ViewMode
              onDelete={() =>
                onConfirmDelete({
                  data: { idx, message: (r as unknown as IIsaacLink).title },
                  message: (r as unknown as IIsaacLink).title
                })
              }
              onEdit={() => handleEdit(idx)}
            />
          ),
          (_, __, idx) => <EditMode onCancel={handleCancel} onSubmit={() => submitEdit(idx)} />
        ])
      }
    ],
    [condition, ISAACTitle, getValues, prefix, control, onConfirmDelete, handleEdit, handleCancel, submitEdit]
  )

  return hidden ? null : (
    <div className={styles.container}>
      <Table
        tableLayout="fixed"
        className={styles.table}
        columns={columns}
        data={data}
        rowKey={({ title }) => title}
        pagination={false}
        onRow={onRow}
      />
      {deleteModal}
      {cancelModal}
    </div>
  )
}
